/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-v / -vi — failed-load surface.
 *
 * Mounted when viewOutfit returns { ok: false }. -vi adds:
 *  - Live Retry CTA wired to useRetryAnalysis (caller-supplied onRetry)
 *  - Email support CTA via mailto (same shape as PendingStatusStrip)
 *  - Design-token application: replaces 1a.5-v inline hex literals
 *    (#F8F4EE / #2A1F18 / #6B5F55) with semantic tokens
 *    (background / foreground / muted-foreground / primary / primary-foreground)
 *
 * Inline styles retained where Tailwind utilities would require a deeper
 * audit; CSS variables read from src/styles.css.
 */
import { ResultsTopNav } from "./ResultsTopNav";

interface Props {
  /** Best-effort issue number for nav parity; pass 1 when unknown. */
  issueNumber: number;
  onBack: () => void;
  /** Outfit id of the row that failed to hydrate (for support email). */
  outfitId?: string | null;
  /** Live retry — when supplied, renders a Try again CTA. */
  onRetry?: () => void;
  /** Hide the Retry CTA when the user has exhausted attempts. */
  canRetry?: boolean;
  /** Disable Retry while a request is in flight. */
  retrying?: boolean;
  /**
   * SOCIAL-SHARE-FACEBOOK-1 — raw `lookbook_error` from the outfits row.
   * When it contains `facebook_story_unsupported`, the surface swaps to
   * a Story-specific copy (title, body, no Retry CTA). All other values
   * (or null) render the generic failed-load copy unchanged.
   */
  lookbookError?: string | null;
}

export default function ResultsLoadFailed({
  issueNumber,
  onBack,
  outfitId = null,
  onRetry,
  canRetry = false,
  retrying = false,
  lookbookError = null,
}: Props) {
  const isFacebookStory =
    typeof lookbookError === "string" &&
    lookbookError.includes("facebook_story_unsupported");
  const showRetry = !isFacebookStory && canRetry && typeof onRetry === "function";

  const emoji = isFacebookStory ? "📖" : "👗";
  const title = isFacebookStory
    ? "Stories can't be shared"
    : "We couldn't load this look";
  const body = isFacebookStory
    ? "Facebook Stories disappear after 24 hours. Share a post, reel, or video instead."
    : "Something went wrong opening this outfit. Head back and try again, or pick a different photo or link.";

  const subject = encodeURIComponent(
    `Support: Couldn't load outfit [${outfitId ?? "unknown"}]`,
  );
  const mailto = `mailto:support@like2fit.com?subject=${subject}`;

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "hsl(var(--background))",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ResultsTopNav
        issueNumber={issueNumber}
        isSaved={false}
        onBack={onBack}
        onBookmarkTap={() => {
          /* no-op on failed surface */
        }}
        hideBookmark
      />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
          textAlign: "center",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>
          {emoji}
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: "hsl(var(--foreground))",
            margin: 0,
            fontFamily: "Georgia, serif",
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.5,
            color: "hsl(var(--muted-foreground))",
            margin: 0,
            maxWidth: 320,
          }}
        >
          {body}
        </p>


        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
          {showRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              style={{
                padding: "12px 28px",
                borderRadius: 999,
                background: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                border: "none",
                fontSize: 15,
                fontWeight: 500,
                cursor: retrying ? "not-allowed" : "pointer",
                opacity: retrying ? 0.6 : 1,
              }}
            >
              {retrying ? "Retrying…" : "Try again"}
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "12px 28px",
              borderRadius: 999,
              background: showRetry ? "transparent" : "hsl(var(--foreground))",
              color: showRetry ? "hsl(var(--foreground))" : "hsl(var(--background))",
              border: showRetry ? "1px solid hsl(var(--border))" : "none",
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Back
          </button>
        </div>

        <a
          href={mailto}
          style={{
            marginTop: 8,
            fontSize: 13,
            color: "hsl(var(--muted-foreground))",
            textDecoration: "underline",
          }}
        >
          Email support
        </a>
      </div>
    </div>
  );
}
